/**
 * Admin Auth Guard
 * 
 * Protects admin routes by checking authentication status
 * Separate from regular user auth guards
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth, useAdminStore } from '../stores/adminStore';

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * Protects admin routes - redirects to admin login if not authenticated
 */
export function AdminProtectedRoute({ children }: AdminGuardProps) {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const { verifyToken } = useAdminStore();
  const location = useLocation();

  // Verify token on mount and when location changes
  useEffect(() => {
    if (!isAuthenticated) {
      verifyToken();
    }
  }, [isAuthenticated, verifyToken, location.pathname]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Redirect to admin login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/manage" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  return <>{children}</>;
}

/**
 * Redirects authenticated admin users away from login page
 */
export function AdminGuestOnlyRoute({ children }: AdminGuardProps) {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const { verifyToken } = useAdminStore();

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/manage/dashboard" replace />;
  }

  return <>{children}</>;
}